import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Boxes, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import {
  cancelConceptBuild,
  getConceptBuildStatus,
  startConceptBuild,
  type ConceptBuildEvent,
} from '@/lib/api/concepts/build-concepts'
import { apiErrorMessage } from '@/lib/api-client'
import { formatNumber } from '@/lib/utils'

/**
 * Sits above the term cloud (the pane's overview state): how much of the
 * collection has been grouped into concepts, and the button that groups it.
 *
 * The build itself is a detached server job, so this bar is a *view* of it,
 * not its owner: navigate away and back and the poll reattaches, replaying
 * missed concept events into the cloud. Any viewer sees a running build;
 * only editors can start one.
 */
export function ConceptBuildBar({
  collectionId,
  grouped,
  total,
  unchecked = 0,
  canEdit,
  onEvent,
  onRunningChange,
}: {
  collectionId: number
  grouped: number
  total: number
  /** Terms no build has judged yet. */
  unchecked?: number
  canEdit: boolean
  /** Concept-formed events, for the cloud animation behind this bar. */
  onEvent?: (e: ConceptBuildEvent) => void
  onRunningChange?: (running: boolean) => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  // Last event seq already replayed into the cloud; the poll asks only for newer.
  const sinceRef = useRef(0)
  // Completion is a transition, not a state — only toast when we saw it running.
  const wasRunning = useRef(false)

  useEffect(() => {
    sinceRef.current = 0
    wasRunning.current = false
  }, [collectionId])

  const status = useQuery({
    queryKey: ['concept-build', collectionId],
    queryFn: () => getConceptBuildStatus(collectionId, sinceRef.current),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 1500 : false),
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    const d = status.data
    if (!d) return
    for (const ev of d.events ?? []) {
      if (ev.seq > sinceRef.current) {
        sinceRef.current = ev.seq
        onEvent?.(ev)
      }
    }
    const running = d.status === 'running'
    onRunningChange?.(running)
    if (running) {
      wasRunning.current = true
    } else if (wasRunning.current) {
      wasRunning.current = false
      if (d.status === 'done') {
        toast.success(t('actions.done'), {
          description: t('concepts.newConcepts', { count: d.result?.concepts ?? 0 }),
        })
      } else if (d.status === 'error') {
        toast.error(t('concepts.buildFailed'), { description: d.message ?? '' })
      }
      // Terms gained concept_ids and the Mõisted tab gained rows.
      qc.invalidateQueries({ queryKey: ['terms'] })
      qc.invalidateQueries({ queryKey: ['concepts'] })
      qc.invalidateQueries({ queryKey: ['collections'] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data])

  const start = async () => {
    try {
      sinceRef.current = 0
      await startConceptBuild(collectionId)
      // Kick the poll instead of waiting out its idle pause.
      qc.invalidateQueries({ queryKey: ['concept-build', collectionId] })
    } catch (e) {
      toast.error(t('concepts.buildFailed'), { description: apiErrorMessage(e) })
    }
  }

  const running = status.data?.status === 'running'
  const frame = status.data?.frame

  // Ticks locally between polls, so the clock moves every second rather than
  // in 1.5s jumps. Seeded from the server so it is right after a reload.
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    if (!running) return
    setElapsed(status.data?.elapsed ?? 0)
    const id = setInterval(() => setElapsed((e) => e + 1), 1000)
    return () => clearInterval(id)
  }, [running, status.data?.elapsed])

  const pct = total ? Math.round((grouped / total) * 100) : 0

  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-2">
      {running ? (
        <>
          <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-1.5 text-[11px]">
              {/* Name the stage. Three different things happen under one
                  progress bar — writing glosses, embedding them, grouping —
                  and they have very different durations, so an unlabelled bar
                  that restarts twice reads as a bar that is stuck. */}
              <span className="font-medium">
                {t(`concepts.stage_${frame?.stage ?? 'starting'}` as 'concepts.stage_glossing')}
              </span>
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, '0')}
              </span>
              {/* And what it is on right now, the way the ingest bar names the
                  file it is processing. */}
              {frame?.current && (
                <span className="truncate text-muted-foreground">{frame.current}</span>
              )}
              {!!frame?.total && (
                <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
                  {formatNumber(frame.index ?? 0)}/{formatNumber(frame.total)}
                  {frame.stage === 'embedding' && ` ${t('units.termsShort')}`}
                  {!!frame.concepts && (
                    <> · {t('units.concepts', { count: frame.concepts })}</>
                  )}
                </span>
              )}
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${Math.max(frame?.percentage ?? 0, 0)}%` }}
              />
            </div>
          </div>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={async () => {
                try {
                  await cancelConceptBuild(collectionId)
                  toast.info(t('concepts.cancelling'))
                  qc.invalidateQueries({ queryKey: ['concept-build', collectionId] })
                } catch (e) {
                  toast.error(apiErrorMessage(e))
                }
              }}
            >
              {t('common.cancel')}
            </Button>
          )}
        </>
      ) : (
        <>
          {/* This is COVERAGE, not progress, and the two used to look
              identical — a bar plus a percentage. A finished build therefore
              read as a job stuck at 45%. It will never reach 100%: a term with
              no equivalent in another language and no synonym is a singleton
              by design, judged and deliberately left alone. So say what the
              number means, and keep the bar visibly slighter than the running
              one. */}
          <Tooltip label={t('concepts.coverageHint')}>
            <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
              {t('concepts.groupedStat', {
                grouped: formatNumber(grouped),
                total: formatNumber(total),
              })}
              <span className="h-1 w-24 overflow-hidden rounded-full bg-muted">
                <span
                  className="block h-full rounded-full bg-muted-foreground/40"
                  style={{ width: `${pct}%` }}
                />
              </span>
              {pct}%
            </span>
          </Tooltip>

          {/* What is actually left to do — the number the button acts on. */}
          <span className="text-[11px] text-muted-foreground">
            ·{' '}
            {unchecked
              ? t('concepts.uncheckedStat', { count: unchecked })
              : t('concepts.allChecked')}
          </span>

          {canEdit && (
            <Tooltip label={t('concepts.buildHint')}>
              <Button
                variant={unchecked ? 'outline' : 'ghost'}
                size="sm"
                className="ml-auto"
                onClick={start}
              >
                <Boxes />
                {unchecked ? t('concepts.build') : t('concepts.recheck')}
              </Button>
            </Tooltip>
          )}
        </>
      )}
    </div>
  )
}
